<script lang="ts" setup>
import countTo from 'vue-count-to'
import { onUnmounted, ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { VIP_TYPE } from '@/constants'
import { SubscriptionCycle } from '@/utils/enum'

defineProps<{
  config: SubscribePageConfig
}>()

const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)
const discountModel = ref(false)
const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount = ref<MemberTopupSubscribeProduct | null>(null)
const discountNumber = ref(0)

function getDiscountPercentage() {
  if (!subscribeProducts.value?.length)
    return false

  let maxDiscount = 0
  let current: MemberTopupSubscribeProduct | null = null

  subscribeProducts.value.forEach((item: any) => {
    let discount = 0
    let shouldCalculate = false

    if (item.subscribeType === VIP_TYPE.ALL && item.preferentialPrice !== undefined && item.preferentialPrice !== null) {
      discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
      shouldCalculate = true
    }

    if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
      discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
      shouldCalculate = true
    }

    if (shouldCalculate && discount >= maxDiscount) {
      current = item
      maxDiscount = Math.round(discount)
    }
  })

  if (current)
    return [current, maxDiscount] as const

  return false
}

function initDiscountModel() {
  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      if (timer.value)
        clearTimeout(timer.value)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}

function buyNow() {
  if (currentDiscount.value)
    onSubscribeProducts(currentDiscount.value)
}

function getCardType(item: MemberTopupSubscribeProduct): 'discount' | 'sale' | 'standard' {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return 'discount'

  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null && !isZero(item.preferentialPrice))
    return 'sale'

  return 'standard'
}

function getDisplayPrice(item: MemberTopupSubscribeProduct) {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return item.preferentialPrice || item.price

  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null && !isZero(item.preferentialPrice))
    return item.preferentialPrice

  return item.price
}

function getOriginalPrice(item: MemberTopupSubscribeProduct) {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return item.discountOriginalPrice

  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null)
    return item.price

  return null
}

function selectProduct(item: MemberTopupSubscribeProduct, index: number) {
  active.value = index
  onSubscribeProducts(item)
}

function getPlanType(item: MemberTopupSubscribeProduct): 'weekly' | 'monthly' | 'seasonal' | 'annual' | 'other' {
  switch (item.cycle) {
    case SubscriptionCycle.WEEK:
      return 'weekly'
    case SubscriptionCycle.MONTH:
      return 'monthly'
    case SubscriptionCycle.SEASON:
      return 'seasonal'
    case SubscriptionCycle.YEAR:
      return 'annual'
    default:
      return 'other'
  }
}

initDiscountModel()

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
})
</script>

<template>
  <div class="talewave-subscribe-page">
    <BasicNavbar class="color-n-text-2" />

    <div class="content-wrapper">
      <!-- 背景层：第二层渐变色 + 第三层背景图片 -->
      <div class="bg-layer">
        <div class="bg-gradient" />
        <img class="bg-image" src="/images/talewave/vip/tw_bg_vip.png" alt="">
      </div>

      <!-- 第四层：标题、描述 -->
      <div class="text-content">
        <h1 class="hero-title">
          {{ config.texts.vipMembership }}
        </h1>
        <p class="hero-desc">
          {{ config.texts.membershipDesc }}
        </p>
        <p class="hero-hint">
          {{ config.texts.notJoinedYet }}
        </p>
      </div>

      <!-- 第四层：档位卡片 -->
      <div class="products-list">
        <div v-for="(item, index) in subscribeProducts" :key="index">
          <article
            v-if="item.isShow"
            class="plan-card"
            :class="[
              `plan-card--${getPlanType(item)}`,
              { 'is-active': active === index },
            ]"
            @click="selectProduct(item, index)"
          >
            <!-- 角标 -->
            <div v-if="item.cornerTip && item.marketingCopy" class="plan-badge">
              {{ item.marketingCopy }}
            </div>

            <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
              <div class="plan-header">
                <div class="plan-header-left">
                  <span class="plan-icon" />
                  <span class="plan-title">{{ item.cycleStr }}</span>
                </div>
                <div class="plan-price">
                  $<countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(getDisplayPrice(item))"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
              </div>
              <div class="plan-body">
                <span class="plan-desc">{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</span>
                <span class="plan-unit plan-unit--line-through">$ {{ getOriginalPrice(item) }}</span>
              </div>
            </template>

            <template v-else-if="getCardType(item) === 'sale'">
              <div class="plan-header">
                <div class="plan-header-left">
                  <span class="plan-icon" />
                  <span class="plan-title">{{ item.cycleStr }}</span>
                </div>
                <div class="plan-price">
                  $<countTo
                    :start-val="Number(item.price)"
                    :end-val="Number(getDisplayPrice(item))"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
              </div>
              <div class="plan-body">
                <span class="plan-desc">{{ item.subscribeText }}</span>
                <span class="plan-unit plan-unit--line-through">$ {{ getOriginalPrice(item) }}</span>
              </div>
            </template>

            <template v-else>
              <div class="plan-header">
                <div class="plan-header-left">
                  <span class="plan-icon" />
                  <span class="plan-title">{{ item.cycleStr }}</span>
                </div>
                <div class="plan-price">
                  <template v-if="isZero(item.preferentialPrice)">
                    {{ config.texts.free }}
                  </template>
                  <template v-else>
                    $ {{ getDisplayPrice(item) }}
                  </template>
                </div>
              </div>
              <div class="plan-body">
                <span class="plan-desc">{{ item.subscribeText }}</span>
                <span
                  v-if="getOriginalPrice(item)"
                  class="plan-unit plan-unit--line-through"
                >$ {{ getOriginalPrice(item) }}</span>
                <span
                  v-else-if="getUnitPrice(item)"
                  class="plan-unit"
                >$ {{ getUnitPrice(item) }}</span>
              </div>
            </template>
          </article>
        </div>
      </div>

      <!-- 第四层：支付方式选择器 -->
      <div v-if="showPaymentMethodSelector" class="payment-methods">
        <PaymentMethodSelector
          variant="large"
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>

      <!-- 第四层：Tips -->
      <div class="tips-section">
        <p class="tips-title">
          {{ config.texts.tips }}
        </p>
        <p class="tips-item">
          {{ config.texts.tip1 }}
        </p>
        <p class="tips-item">
          {{ config.texts.tip2 }}
        </p>
      </div>
    </div>

    <!-- 底部渐变遮罩 -->
    <div class="bottom-gradient" />

    <BasicModal v-model="discountModel" :transparent="true" class-name="discount-modal">
      <div @click="buyNow">
        <div class="w-[200px] flex justify-center absolute top-[40px] left-[50%] ml-[-100px]">
          <div class="text-white text-30px font-bold italic">
            {{ discountNumber }}<span class="text-20px">%</span>
            <div class="relative top-[-5px]">
              OFF
            </div>
          </div>
        </div>
        <img :src="config.resources.discountBg" width="300px">
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.talewave-subscribe-page {
  min-height: 100vh;
  background: #FFFFFF;
  position: relative;
  padding-bottom: 85px;
}

/* 内容容器 */
.content-wrapper {
  position: relative;
  padding: 0 14px 24px;
}

/* 背景层 */
.bg-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
}

/* 第二层：背景图片 */
.bg-image {
  position: relative;
  z-index: 1;
  width: 100%;
  display: block;
}

/* 第三层：渐变色 */
.bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

/* 第四层：标题和描述 */
.text-content {
  position: relative;
  z-index: 3;
  padding-top: 16px;
}

.hero-title {
  margin: 0;
  font-size: 18px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  color: rgba(50, 53, 58, 1);
  line-height: 16px;
  text-align: center;
}

.hero-desc {
  margin: 20px 0 0;
  font-size: 14px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  color: rgba(112, 75, 44, 1);
  line-height: 18px;
}

.hero-hint {
  margin: 10px 0 0;
  font-size: 12px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  color: rgba(247, 73, 132, 1);
  line-height: 12px;
}

/* 第四层：档位卡片 */
.products-list {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

/* 计划卡片 */
.plan-card {
  position: relative;
  border-radius: 8px;
  padding: 17px 16px 19px;
  background: url('/images/talewave/vip/tw_bg_vip_item.png') no-repeat center;
  background-size: 100% 100%;
  border: none;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.plan-card.is-active {
  box-shadow: inset 0 0 0 2px rgba(255, 171, 115, 1), 0 4px 16px rgba(255, 171, 115, 0.25);
  transform: translateY(-1px);
}

/* 角标 */
.plan-badge {
  position: absolute;
  top: -6px;
  left: 0;
  z-index: 1;
  height: 16px;
  padding: 0 8px;
  border-radius: 8px 2px 8px 2px;
  background: rgba(247, 73, 132, 1);
  font-size: 10px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  line-height: 16px;
  color: #ffffff;
  white-space: nowrap;
}

/* 卡片头部 */
.plan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.plan-header-left {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  flex: 1;
}

.plan-icon {
  display: block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: url('/images/talewave/vip/tw_ic_vip.png') no-repeat center;
  background-size: contain;
}

.plan-title {
  font-size: 18px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 700;
  color: rgba(136, 58, 15, 1);
  line-height: 14px;
  white-space: nowrap;
}

.plan-price {
  flex-shrink: 0;
  font-size: 18px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 700;
  color: rgba(136, 58, 15, 1);
  line-height: 14px;
  text-align: right;
  white-space: nowrap;
}

/* 卡片内容 */
.plan-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}

.plan-desc {
  font-size: 14px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  color: rgba(182, 126, 76, 1);
  line-height: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-unit {
  flex-shrink: 0;
  font-size: 12px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  color: rgba(182, 126, 76, 1);
  line-height: 12px;
  text-align: right;
  white-space: nowrap;
}

.plan-unit--line-through {
  text-decoration: line-through;
}

/* 支付方式 */
.payment-methods {
  position: relative;
  z-index: 3;
  margin-top: 20px;
}

.payment-methods :deep(.payment-method-selector-large) {
  background: transparent;
  margin: 0;
  padding: 0;
  justify-content: space-between;
  gap: 10px;
}

.payment-methods :deep(.payment-method-btn-large) {
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border: 1px solid rgba(219, 219, 219, 1);
  border-radius: 8px;
  padding: 8px 16px;
  height: auto;
}

.payment-methods :deep(.payment-method-btn-large.active) {
  background: #ffffff;
  border-color: rgba(83, 162, 255, 1);
}

/* Tips */
.tips-section {
  position: relative;
  z-index: 3;
  margin-top: 14px;
  font-size: 12px;
  font-family: HelveticaNeue, Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 18px;
  color: rgba(150, 156, 170, 1);
}

.tips-title {
  margin: 0;
  font-weight: 400;
}

.tips-item {
  margin: 6px 0 0;
}

/* 底部渐变遮罩 */
.bottom-gradient {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 85px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  pointer-events: none;
  z-index: 1;
}
</style>

<style lang="scss">
.discount-modal {
  .close-icon {
    top: 12px;
    right: 0;
  }
}
</style>
